import classNames from 'classnames';

import React from 'react';

import { useForm, FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import Button from 'reactstrap/lib/Button';
import Col from 'reactstrap/lib/Col';
import Form from 'reactstrap/lib/Form';
import FormGroup from 'reactstrap/lib/FormGroup';
import Row from 'reactstrap/lib/Row';
import Spinner from 'reactstrap/lib/Spinner';

import { resetPassword } from '@ttstr/api';
import FormField from '@ttstr/components/Form/FormField';

interface ResetPasswordFields {
  password: string;
  password_confirmation: string;
}

interface OwnProps {
  token: string;
}

type Props = Readonly<OwnProps>;

const CustomerResetPassword: React.FC<Props> = ({ token }) => {
  const [success, setSuccess] = React.useState<boolean>(null);
  const { t } = useTranslation();
  const formContext = useForm<ResetPasswordFields>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });
  const { handleSubmit, formState, getValues } = formContext;
  const { isSubmitting } = formState;

  async function onSubmit(values: ResetPasswordFields) {
    const { password, password_confirmation } = values;
    try {
      await resetPassword(password, password_confirmation, token);
      setSuccess(true);
    } catch (error) {
      setSuccess(false);
    }
  }

  if (success === true) {
    return (
      <div className="text-center">
        <p className="text-success">{t('PASSWORD_RESET.SUCCESS')}</p>
        <Button tag={Link} to="/customer/login" autoFocus>
          {t('CUSTOMER.LOGIN_NOW')}
        </Button>
      </div>
    );
  }
  if (success === false) {
    return (
      <div className="text-center">
        <p className="text-center text-danger">{t('PASSWORD_RESET.FAILED')}</p>
        <Button type="button" color="primary" onClick={() => setSuccess(null)} autoFocus>
          {t('PASSWORD_RESET.TRY_AGAIN')}
        </Button>
      </div>
    );
  }

  return (
    <FormProvider {...formContext}>
      <Form action="#" onSubmit={handleSubmit(onSubmit)}>
        <fieldset disabled={isSubmitting}>
          <FormGroup tag="fieldset">
            <Row>
              <Col>
                <FormField
                  type="password"
                  label={t('CUSTOMER.PASSWORD')}
                  name="password"
                  helpText={t('CUSTOMER.PASSWORD_HELP', { min: 6 })}
                  autoComplete="new-password"
                  formGroupClassName="mb-0"
                  validationOptions={{
                    required: true,
                    minLength: 6,
                  }}
                />
              </Col>
              <Col>
                <FormField
                  type="password"
                  label={t('CUSTOMER.PASSWORD_REPEAT')}
                  name="password_confirmation"
                  autoComplete="new-password"
                  formGroupClassName="mb-0"
                  validationOptions={{
                    required: true,
                    minLength: 6,
                    validate: {
                      'CUSTOM.PASSWORDS_DONT_MATCH': (value) => String(value) === String(getValues().password),
                    },
                  }}
                />
              </Col>
            </Row>
          </FormGroup>

          <Button color="primary" type="submit" block>
            {t(`PASSWORD_RESET.CONFIRM_RESET`)}
            <span className={classNames('spinner-wrapper', { invisible: !isSubmitting })}>
              <Spinner color="primary" size="sm" />
            </span>
          </Button>
        </fieldset>
      </Form>
    </FormProvider>
  );
};

export default React.memo(CustomerResetPassword);
